// ========================================
// INDEX.JS - Entry Point with MS365 Auth & Routing
// ========================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import ProtectedRoute from './components/ProtectedRoute';
import { initializeMsal } from './services/authService';

// Internationalization - import initializes i18n
import './i18n';

// ========================================
// PRODUCTION SECURITY: Disable all console output
// ========================================
// To enable logs in production for debugging, set REACT_APP_ENABLE_LOGS=true
// when building: REACT_APP_ENABLE_LOGS=true npm run build
const enableLogs = process.env.REACT_APP_ENABLE_LOGS === 'true';

if (process.env.NODE_ENV === 'production' && !enableLogs) {
    const noop = () => { };
    console.log = noop;
    console.info = noop;
    console.warn = noop;
    console.debug = noop;
    console.trace = noop;
    console.dir = noop;
    console.table = noop;
    console.group = noop;
    console.groupEnd = noop;
    console.groupCollapsed = noop;
    console.time = noop;
    console.timeEnd = noop;
    console.timeLog = noop;
    console.count = noop;
    console.countReset = noop;
    console.assert = noop;
    console.clear = noop;
    // Keep console.error for critical runtime errors
}

// Evict any MSAL cache entries that used the old short-form 'User.Read' scope.
// These would have targeted the app's own AAD audience rather than Graph, causing
// photo and other Graph calls to fail with 401. Safe to run on every load —
// once the old entries are gone this loop finds nothing and exits immediately.
for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('msal.')) {
        try {
            const val = sessionStorage.getItem(key);
            if (val && val.includes('"User.Read"') && !val.includes('graph.microsoft.com')) {
                sessionStorage.removeItem(key);
            }
        } catch { /* ignore */ }
    }
}

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize auth service with MSAL instance for API calls
initializeMsal(msalInstance);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <BrowserRouter>
            <MsalProvider instance={msalInstance}>
                <Routes>
                    {/* Main dashboard route */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <App />
                            </ProtectedRoute>
                        }
                    />
                    {/* Auth redirect route (required for MSAL) */}
                    <Route
                        path="/auth/redirect"
                        element={
                            <ProtectedRoute>
                                <App />
                            </ProtectedRoute>
                        }
                    />
                    {/* Redirect all other routes to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </MsalProvider>
        </BrowserRouter>
    </React.StrictMode>
);
